import areExternalScriptsDisabled from './areExternalScriptsDisabled';

const STORAGE_KEY = 'catalog-source';

// Query param on catalog add-to-cart links (ECOM-5701).
export const catalogSourceParam = 'source';

export const digitalCatalogSource = 'digital_catalog';

export interface CatalogSource {
  readonly source: typeof digitalCatalogSource;
}

// The fields added to the `ecommerce` node of funnel events.
export interface CatalogSourceTrackingData {
  readonly source?: typeof digitalCatalogSource;
}

export const parseCatalogSource = (source: string | null | undefined): CatalogSource | null => {
  const catalogSource: CatalogSource | null = source === digitalCatalogSource ? { source } : null;
  return catalogSource;
};

export function clearCatalogSource(): void {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable
    }
  }
}

// Removes a stored value that is not a valid source.
function readEntry(): CatalogSource | undefined {
  let entry: CatalogSource | undefined;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    entry = parseCatalogSource(raw) ?? undefined;
    if (raw !== null && entry === undefined) {
      clearCatalogSource();
    }
  } catch {
    entry = undefined;
  }
  return entry;
}

export function setCatalogSource({ source }: CatalogSource): void {
  if (typeof window !== 'undefined') {
    if (areExternalScriptsDisabled()) {
      clearCatalogSource();
    } else {
      try {
        sessionStorage.setItem(STORAGE_KEY, source);
      } catch {
        // sessionStorage unavailable or quota exceeded
      }
    }
  }
}

// Per tab, until the purchase is tracked: survives reloads and same-tab redirects (e.g. payment),
// but is not shared with tabs opened separately. An opt-out drops a source stored before it was set.
export function getCatalogSource(): CatalogSource | undefined {
  let catalogSource: CatalogSource | undefined;
  if (typeof window !== 'undefined') {
    if (areExternalScriptsDisabled()) {
      clearCatalogSource();
    } else {
      catalogSource = readEntry();
    }
  }
  return catalogSource;
}

export function getCatalogSourceTrackingData(): CatalogSourceTrackingData {
  const catalogSource = getCatalogSource();
  const trackingData: CatalogSourceTrackingData =
    catalogSource === undefined ? {} : { source: catalogSource.source };
  return trackingData;
}
